<template>
  <md-card>
    <md-card-content>
      <div class="md-layout md-alignment-middle-left price-row booking-status">
        <div class="md-layout-item md-size-10">
          <booking-status
            :status-value="financials.status.value"
            :status-description="financials.status.description"
            :show-text="false"
          />
          <md-tooltip md-direction="top">Booking Status</md-tooltip>
        </div>

        <div class="md-layout-item md-size-50">
          <span class="md-subheading">{{ financials.status.description }}</span>
        </div>

        <div
          class="md-layout-item md-size-40 text-right"
          v-if="allowReserveBooking"
        >
          <md-button
            class="md-dense md-raised md-primary"
            @click="reserveBooking"
          >
            Reserve Booking
          </md-button>
        </div>

        <div
          class="md-layout-item md-size-40 text-right"
          v-if="allowBookingInvoice"
        >
          <md-button
            class="md-dense md-raised md-success"
            @click.prevent="sendInvoice"
          >
            Email Invoice
          </md-button>
        </div>

        <div
          class="md-layout-item md-size-40 text-right"
          v-if="allowBookingConfirm"
        >
          <md-button
            class="md-dense md-raised md-success"
            @click.prevent="confirmBooking"
          >
            Confirm Booking
          </md-button>
        </div>
      </div>

      <div class="md-layout md-alignment-middle-left price-row booking-amounts">
        <div class="md-layout-item md-size-100" style="margin-top: 1em;">
          <h6>Booking Amounts</h6>
        </div>

        <div class="md-layout-item md-size-33">
          <md-field>
            <md-icon
              ><i
                :class="currencyClass"
                class="fas text-primary hi-price-icon"
              ></i
            ></md-icon>
            <label>Travel</label>
            <md-input v-model="financials.travel_total" disabled></md-input>
          </md-field>
        </div>

        <div class="md-layout-item md-size-33">
          <md-field>
            <md-icon
              ><i
                :class="currencyClass"
                class="fas text-primary hi-price-icon"
              ></i
            ></md-icon>
            <label>Extras</label>
            <md-input v-model="financials.extras_total" disabled></md-input>
          </md-field>
        </div>

        <div class="md-layout-item md-size-33">
          <md-field>
            <md-icon
              ><i
                :class="currencyClass"
                class="fas text-primary hi-price-icon"
              ></i
            ></md-icon>
            <label>Discount</label>
            <md-input v-model="financials.discount" disabled></md-input>
          </md-field>
        </div>

        <div class="md-layout-item md-size-33">
          <md-field>
            <md-icon
              ><i
                :class="currencyClass"
                class="fas text-primary hi-price-icon"
              ></i
            ></md-icon>
            <label>Total</label>
            <md-input v-model="financials.booking_total" disabled></md-input>
          </md-field>
        </div>

        <div class="md-layout-item md-size-33">
          <md-field>
            <md-icon
              ><i
                :class="currencyClass"
                class="fas text-primary hi-price-icon"
              ></i
            ></md-icon>
            <label>Deposit</label>
            <md-input v-model="financials.deposit" disabled></md-input>
          </md-field>
        </div>

        <div class="md-layout-item md-size-33">
          <md-field>
            <md-icon
              ><i
                :class="currencyClass"
                class="fas text-primary hi-price-icon"
              ></i
            ></md-icon>
            <label>Paid</label>
            <md-input v-model="financials.paid" disabled></md-input>
          </md-field>
        </div>

        <div class="md-layout-item md-size-33">
          <md-field>
            <md-icon
              ><i
                :class="currencyClass"
                class="fas text-primary hi-price-icon"
              ></i
            ></md-icon>
            <label>Balance</label>
            <md-input v-model="financials.balance" disabled></md-input>
          </md-field>
        </div>

        <div class="md-layout-item md-size-66">
          <md-field>
            <md-icon>
              <i class="fas fa-calendar-check text-primary hi-price-icon"></i>
            </md-icon>
            <label>Balance Due Date</label>
            <md-input
              :value="financials.due_date | formatDate"
              disabled
            ></md-input>
          </md-field>
        </div>
      </div>

      <booking-discount :booking="booking" @updated="discountsChanged" />

      <div class="md-layout" style="margin-top: 1em;">
        <div class="md-layout-item md-size-90"><h6>Transactions</h6></div>
        <div class="md-layout-item md-size-10 text-right">
          <md-button
            class="md-icon-button md-dense md-primary md-round"
            @click="showTransactionDialog = true"
          >
            <md-icon>add</md-icon>
          </md-button>
        </div>
      </div>

      <div id="transactions">
        <div class="transaction" v-for="trx in ts" :key="trx.id">
          <div class="md-layout">
            <div class="md-layout-item md-size-100 md-caption">
              {{ trx.created_at | formatDate }} | Ref: {{ trx.reference }}
              <span v-if="trx.meta.length">
                | <a href="#" @click.prevent="metaDialog(trx.meta)">Meta</a>
              </span>
            </div>
          </div>

          <div class="md-layout mt-1">
            <div class="md-layout-item md-size-10">
              <md-icon>
                <md-tooltip>{{ trx.status }}</md-tooltip>
                <i
                  v-if="trx.status == 'Success'"
                  class="fas fa-check-square text-success hi-price-icon"
                ></i>
                <i
                  v-else
                  class="fas fa-exclamation-triangle text-danger hi-price-icon"
                ></i>
              </md-icon>
            </div>

            <div class="md-layout-item md-size-30">
              <md-icon
                ><i
                  :class="currencyClass"
                  class="fas text-primary hi-price-icon"
                ></i
              ></md-icon>
              <span :class="{ credit: trx.amount < 0 }">{{ trx.amount }}</span>
            </div>

            <div class="md-layout-item md-size-50">
              {{ trx.type.description }}
            </div>

            <div class="md-layout-item md-size-10 text-right">
              <md-button
                class="md-icon-button md-dense md-danger md-simple"
                @click.stop="removeTransaction(trx)"
              >
                <md-icon>clear</md-icon>
                <md-tooltip>Remove this Transaction</md-tooltip>
              </md-button>
            </div>
          </div>
        </div>
      </div>

      <md-dialog :md-active.sync="showTransactionDialog">
        <md-dialog-title>Post a Transaction</md-dialog-title>
        <md-dialog-content>
          <ValidationObserver v-slot="{ handleSubmit }">
            <form @submit.prevent="handleSubmit(addTransaction)" class="mt-4">
              <div class="md-layout">
                <div class="md-layout-item md-size-50">
                  <md-field>
                    <label>Amount</label>
                    <md-input
                      v-model="transaction.amount"
                      type="text"
                    ></md-input>
                  </md-field>
                </div>

                <div class="md-layout-item md-size-50">
                  <md-field>
                    <label for="type">Type</label>
                    <md-select v-model="transaction.type" name="type" id="type">
                      <md-option value="customer_gateway"
                        >Customer Gateway</md-option
                      >
                      <md-option value="bank_transfer">Bank Transfer</md-option>
                      <md-option value="cc_phone"
                        >Credit Card [Phone]</md-option
                      >
                      <md-option value="internal_transfer"
                        >Internal Transfer</md-option
                      >
                      <md-option value="manual">Manual</md-option>
                      <md-option value="credit">Credit</md-option>
                    </md-select>
                  </md-field>
                </div>

                <div class="md-layout-item md-size-100">
                  <md-field>
                    <label>Reference</label>
                    <md-input
                      v-model="transaction.reference"
                      type="text"
                    ></md-input>
                  </md-field>
                </div>
              </div>
              <md-dialog-actions>
                <md-button
                  class="md-primary md-dense"
                  @click="showTransactionDialog = false"
                  >Cancel</md-button
                >
                <md-button type="submit" class="md-primary md-dense"
                  >Post</md-button
                >
              </md-dialog-actions>
            </form>
          </ValidationObserver>
        </md-dialog-content>
      </md-dialog>

      <md-dialog :md-active.sync="showMetaDialog">
        <md-dialog-title>Transaction MetaData</md-dialog-title>
        <div class="md-layout">
          <div class="md-layout-item md-size-100">
            <pre id="meta-content">{{ this.meta }}</pre>
          </div>
        </div>
        <md-dialog-actions>
          <md-button class="md-primary md-dense" @click="showMetaDialog = false"
            >Done</md-button
          >
        </md-dialog-actions>
      </md-dialog>
    </md-card-content>
  </md-card>
</template>

<script>
import bookingApi from "@/services/api/booking";
import confirm from "@/helpers/deleteConfirm";
import _filter from "lodash/filter";
import BookingDiscount from "./BookingDiscount.vue";
import BookingStatus from "@/components/BookingStatus.vue";
import moment from "moment";
import noteApi from "@/services/api/note";

export default {
  props: ["booking", "financials"],

  components: { BookingDiscount, BookingStatus },

  data() {
    return {
      transaction: {},
      ts: [],
      showTransactionDialog: false,
      showMetaDialog: false,
      meta: "",
      hideReserveButton: false
    };
  },

  computed: {
    currencyClass() {
      return {
        gbp: "fa-pound-sign",
        usd: "fa-dollar-sign",
        eur: "fa-euro-sign"
      }[this.booking.currency];
    },

    allowBookingInvoice() {
      const goodState = this.financials.status.value === "reserved";
      const positiveBalance = parseFloat(this.financials.balance) > 0.0;

      return goodState && positiveBalance;
    },

    allowBookingConfirm() {
      const goodState =
        this.financials.status.value === "pending" ||
        this.financials.status.value === "reserved";
      const zeroBalance = parseFloat(this.financials.balance) <= 0.0;

      return goodState && zeroBalance;
    },

    allowReserveBooking() {
      return (
        (this.financials.status.value === "pending" ||
          this.financials.status.value === "abandoned") &&
        !this.hideReserveButton
      );
    }
  },

  methods: {
    discountsChanged() {
      this.$emit("bookingChanged", this.booking);
    },

    addTransaction() {
      bookingApi
        .postTransaction(this.booking, this.transaction)
        .then(response => {
          this.ts.push(response.data);
          this.transaction = {};
          this.showTransactionDialog = false;

          this.$emit("bookingChanged", this.booking);
        });
    },

    removeTransaction(transaction) {
      confirm.delete(null).then(resp => {
        if (resp.value) {
          bookingApi.deleteTransaction(this.booking, transaction).then(() => {
            this.ts = _filter(this.ts, el => el.id !== transaction.id);

            this.$emit("bookingChanged", this.booking);

            this.$notify({
              timeout: 2500,
              message: "Transaction Removed",
              icon: "thumb_up",
              horizontalAlign: "center",
              verticalAlign: "top",
              type: "warning"
            });
          });
        }
      });
    },

    getTransactions() {
      bookingApi.getTransactions(this.booking).then(response => {
        this.ts = response.data;
      });
    },

    metaDialog(meta) {
      this.meta = meta;
      this.showMetaDialog = true;
    },

    sendInvoice() {
      bookingApi
        .sendBookingInvoice(this.booking.id)
        .then(() => {
          this.$notify({
            timeout: 2500,
            message: "Invoice sent.",
            icon: "thumb_up",
            horizontalAlign: "center",
            verticalAlign: "top",
            type: "success"
          });
        })
        .then(() => {
          let user = this.$store.state.auth.user;
          const date = moment().format("YYYY-MM-DD HH:mm");
          this.sendNote({
            content: `Invoice sent on ${date} by ${user.full_name}`,
            id: null
          });
        });
    },

    sendNote(note) {
      const notableEntity = {
        entityType: "booking",
        entityId: this.booking.id
      };
      noteApi.saveNote({ ...note, ...notableEntity });
    },

    async confirmBooking() {
      try {
        const confirmed = await bookingApi.confirmBooking(this.booking);
        this.$emit("bookingChanged");
        this.$notify({
          timeout: 2500,
          message: "Booking successfully Confirmed.",
          icon: "check",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success"
        });
      } catch (error) {
        this.$notify({
          timeout: 3000,
          message: "Failed to confirm the booking.",
          icon: "error",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      }
    },

    async reserveBooking() {
      try {
        const updated = await bookingApi.reserveBooking(this.booking);

        this.hideReserveButton = true;

        this.$notify({
          timeout: 2500,
          message: "Booking successfully moved to Reserved.",
          icon: "check",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "success"
        });

        this.$emit("bookingChanged", updated);
      } catch (error) {
        this.$notify({
          timeout: 3000,
          message: "Failed to reserve the booking.",
          icon: "error",
          horizontalAlign: "center",
          verticalAlign: "top",
          type: "danger"
        });
      }
    }
  },
  created() {
    this.getTransactions();
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/md/_variables.scss";
#meta-content {
  max-width: 600px;
  max-height: 400px;
  overflow: auto;
}
.transaction {
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #00bcd4;
  margin-bottom: 0.5rem;
  .md-button {
    height: 15px;
  }
}
.credit {
  color: red;
}
</style>
